import BlogInertiaController from '@/actions/App/Http/Controllers/BlogInertiaController';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { NewsItem } from '@/types/search';
import { Link } from '@inertiajs/react';
import { ArrowRight, Calendar } from 'lucide-react';

interface NewsMasonrySectionProps {
  newsItems?: NewsItem[];
  className?: string;
}

/**
 * Editorial masonry for "Tin tức & Khám phá".
 *
 * CSS columns rather than a JS-measured layout: it needs no resize observer, survives
 * SSR without a hydration mismatch, and matches the pattern already used by
 * `components/blog/blog-masonry-grid.tsx`.
 *
 * The trade-off CSS columns bring is column-major fill — post 1..5 stack down column one
 * instead of reading left-to-right by recency. Rather than fight that with JS, recency is
 * carried by *treatment*: the newest post wears a "Mới nhất" badge.
 *
 * Card heights are what make a masonry read as a masonry. A single fixed aspect ratio
 * produces a ragged-but-boring grid, so image ratios cycle deterministically by index.
 */

/** Cycled so adjacent cards rarely share a height. Deterministic — SSR-safe. */
const IMAGE_RATIOS = ['aspect-[4/5]', 'aspect-square', 'aspect-[3/4]', 'aspect-[16/10]'] as const;

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });

export function NewsMasonrySection({ newsItems, className }: NewsMasonrySectionProps) {
  const items = newsItems ?? [];
  if (items.length === 0) return null;

  return (
    <section className={cn('relative', className)} aria-labelledby="news-masonry-heading">
      {/* Header mirrors SectionRail's so this section still rhymes with its neighbours. */}
      <header className="mb-4 flex items-end justify-between gap-4 md:mb-5">
        <div className="flex min-w-0 items-center gap-3">
          <span aria-hidden="true" className="block h-6 w-[3px] shrink-0 rounded-full" style={{ backgroundColor: 'var(--section-accent)' }} />
          <div className="min-w-0">
            <h2 id="news-masonry-heading" className="truncate text-xl leading-tight font-bold text-gray-900 md:text-2xl">
              Tin tức &amp; Khám phá
            </h2>
            <p className="mt-0.5 truncate text-sm text-gray-500">Cập nhật những điểm đến mới và trải nghiệm thú vị nhất</p>
          </div>
        </div>

        <Link
          href={BlogInertiaController.index().url}
          className="text-primary hover:text-primary/80 hidden shrink-0 items-center gap-0.5 text-sm font-medium transition-colors sm:inline-flex"
        >
          Xem tất cả
          <ArrowRight className="size-4" />
        </Link>
      </header>

      <div className="columns-1 gap-4 sm:columns-2 md:gap-5 lg:columns-3 xl:columns-4">
        {items.map((item, index) => (
          <NewsMasonryCard key={item.id} item={item} ratio={IMAGE_RATIOS[index % IMAGE_RATIOS.length]!} isLatest={index === 0} />
        ))}
      </div>

      <div className="mt-8 flex justify-center">
        <Button variant="outline" size="lg" className="rounded-full px-8" asChild>
          <Link href={BlogInertiaController.index().url}>
            Xem tất cả bài viết
            <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Button>
      </div>
    </section>
  );
}

function NewsMasonryCard({ item, ratio, isLatest }: { item: NewsItem; ratio: string; isLatest: boolean }) {
  return (
    // `break-inside-avoid` is what keeps a card from being sliced across a column boundary.
    <Link
      href={BlogInertiaController.show(item.slug).url}
      className="group focus-visible:ring-ring mb-4 block break-inside-avoid overflow-hidden rounded-2xl border border-gray-100 bg-white transition-shadow duration-300 hover:shadow-lg focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none md:mb-5"
    >
      <div className={cn('relative overflow-hidden bg-gray-100', ratio)}>
        {item.featuredImage ? (
          <img
            src={item.featuredImage}
            alt={item.title}
            loading="lazy"
            decoding="async"
            className="size-full object-cover transition-transform duration-500 group-hover:scale-105 motion-reduce:transition-none"
          />
        ) : (
          <div className="flex size-full items-center justify-center text-gray-300">
            <Calendar className="size-10" aria-hidden="true" />
          </div>
        )}

        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {isLatest && <Badge className="border-0 bg-gray-900 text-white">Mới nhất</Badge>}
          {item.category && <Badge className="bg-primary text-primary-foreground border-0">{item.category}</Badge>}
        </div>
      </div>

      <div className="space-y-2 p-4">
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Calendar className="size-3.5" aria-hidden="true" />
          {formatDate(item.publishedAt)}
        </div>
        <h3 className="group-hover:text-primary line-clamp-2 text-base leading-snug font-semibold text-gray-900 transition-colors">{item.title}</h3>
        {item.excerpt && <p className="line-clamp-3 text-sm leading-relaxed text-gray-600">{item.excerpt}</p>}
      </div>
    </Link>
  );
}
